import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ChevronLeft, Check, Trash2, Pencil } from "lucide-react";

const Overview = () => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/programs/overview").then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s) return <div className="text-slate-500 font-mono text-sm animate-pulse">Loading…</div>;
  const cards = [["Total programs", s.total], ["Active", s.active], ["Completed", s.completed],
    ["Completion rate", `${s.completion_rate}%`], ["Check-ins", s.checkins], ["Meal swaps", s.meal_swaps],
    ["12-week", s.duration_split["12"]], ["24-week", s.duration_split["24"]], ["Review queue", s.review_queue]];
  return (
    <div data-testid="prog-overview">
      <h1 className="font-serif text-4xl text-white mb-6">Programs Overview</h1>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {cards.map(([l, v]) => (
          <div key={l} className="glass rounded-2xl p-5"><div className="font-mono text-2xl text-cyan-300">{v}</div><div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-1">{l}</div></div>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        <div className="glass rounded-2xl p-5"><div className="mono-label mb-3">By program type</div>{Object.entries(s.by_type).map(([k, v]) => <div key={k} className="flex justify-between text-sm py-1"><span className="text-slate-300 capitalize">{k.replace("_", " ")}</span><span className="font-mono text-cyan-300">{v}</span></div>)}</div>
        <div className="glass rounded-2xl p-5"><div className="mono-label mb-3">By status</div>{Object.entries(s.by_status).map(([k, v]) => <div key={k} className="flex justify-between text-sm py-1"><span className="text-slate-300">{k}</span><span className="font-mono text-cyan-300">{v}</span></div>)}</div>
      </div>
    </div>
  );
};

const WeekEditor = ({ accessId }) => {
  const [n, setN] = useState(1);
  const [week, setWeek] = useState(null);
  const load = async () => { try { const { data } = await api.get(`/admin/programs/week/${accessId}/${n}`); setWeek(data); } catch { toast.error("Week not found"); } };
  const save = async (di, t, patch) => {
    await api.post("/admin/programs/edit-task", { access_id: accessId, week_number: n, day_index: di, task_id: t.id, ...patch, reason: "admin edit" });
    toast.success("Saved"); load();
  };
  return (
    <div className="glass rounded-2xl p-5 mt-4" data-testid="admin-week-editor">
      <div className="flex items-center gap-2 mb-3">
        <span className="mono-label">Edit week</span>
        <Input type="number" value={n} onChange={(e) => setN(Number(e.target.value))} className="w-20 bg-black/40 border-white/10 h-8" data-testid="admin-week-num" />
        <Button size="sm" onClick={load} className="btn-primary rounded-lg" data-testid="admin-week-load">Load</Button>
      </div>
      {week && week.days.map((d, di) => (
        <div key={di} className="mb-3">
          <div className="text-sm text-white mb-1">{d.day_name}</div>
          {d.tasks.map((t) => (
            <div key={t.id} className="flex items-center gap-2 mb-1" data-testid={`admin-task-${t.id}`}>
              <Input defaultValue={t.title} onBlur={(e) => e.target.value !== t.title && save(di, t, { title: e.target.value })} className="bg-black/40 border-white/10 h-8 text-xs flex-1" />
              <button onClick={() => save(di, t, { done: !t.done })} className={`w-7 h-7 rounded border flex items-center justify-center ${t.done ? "bg-cyan-400 border-cyan-400" : "border-white/20"}`}>{t.done && <Check size={13} className="text-black" />}</button>
              <button onClick={() => save(di, t, { delete: true })} className="p-1.5 text-rose-400"><Trash2 size={13} /></button>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

const UserDetail = ({ userId, onBack }) => {
  const [d, setD] = useState(null);
  const [note, setNote] = useState("");
  const [editAccess, setEditAccess] = useState(null);
  const load = () => api.get(`/admin/programs/user/${userId}`).then(({ data }) => setD(data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);
  if (!d) return <div className="text-slate-500 font-mono text-sm animate-pulse">Loading…</div>;

  const addNote = async () => { if (!note.trim()) return; await api.post("/admin/programs/note", { user_id: userId, text: note }); setNote(""); load(); toast.success("Note added"); };
  const review = async (accessId, decision) => { await api.post("/admin/programs/review", { access_id: accessId, decision }); toast.success("Updated"); load(); };
  const grant = async (pt) => { await api.post("/admin/programs/grant", { user_id: userId, program_type: pt, duration_weeks: 12 }); toast.success("Granted"); load(); };

  return (
    <div data-testid="admin-user-program">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-400 mb-3"><ChevronLeft size={16} /> Back</button>
      <h1 className="font-serif text-3xl text-white">{d.user?.name}</h1>
      <p className="text-sm text-slate-400 mb-4">{d.user?.email} · {d.user?.role}</p>

      <div className="glass rounded-2xl p-5 mb-4">
        <div className="mono-label mb-3">Program access</div>
        {d.accesses.map((a) => (
          <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 py-2 border-b border-white/5" data-testid={`admin-access-${a.id}`}>
            <span className="text-sm text-slate-200 capitalize">{a.program_type.replace("_", " ")} · {a.duration_weeks}w</span>
            <span className="stat-pill">{a.status}</span>
            <div className="flex gap-2">
              {a.status === "REQUIRES_REVIEW" && <>
                <Button size="sm" onClick={() => review(a.id, "approve")} className="btn-primary rounded-lg text-xs" data-testid={`review-approve-${a.id}`}>Approve</Button>
                <Button size="sm" variant="outline" onClick={() => review(a.id, "reject")} className="text-rose-400 text-xs">Reject</Button>
              </>}
              {["ACTIVE", "PAUSED"].includes(a.status) && <Button size="sm" variant="outline" onClick={() => setEditAccess(a.id === editAccess ? null : a.id)} data-testid={`admin-edit-program-${a.id}`}><Pencil size={12} className="mr-1" /> Edit plan</Button>}
            </div>
          </div>
        ))}
        <div className="flex gap-2 mt-3">
          {["weight_loss", "performance", "longevity"].map((pt) => <Button key={pt} size="sm" variant="outline" onClick={() => grant(pt)} data-testid={`admin-grant-${pt}`} className="text-xs capitalize">+ {pt.replace("_", " ")}</Button>)}
        </div>
      </div>

      {editAccess && <WeekEditor accessId={editAccess} />}

      <div className="glass rounded-2xl p-5 mb-4">
        <div className="mono-label mb-2">Health flags {!d.can_view_health && <span className="text-amber-400">(restricted)</span>}</div>
        {(d.health?.flags || []).length === 0 && <div className="text-sm text-slate-500">No flags.</div>}
        {(d.health?.flags || []).map((f, i) => <div key={i} className="text-sm text-slate-300">{f.outcome || JSON.stringify(f)}</div>)}
      </div>

      <div className="glass rounded-2xl p-5">
        <div className="mono-label mb-2">Coach notes</div>
        {d.notes.map((nt) => <div key={nt.id} className="text-sm text-slate-300 border-b border-white/5 py-2"><span className="text-slate-500 text-[11px] font-mono">{nt.author} · {nt.created_at?.slice(0, 10)}</span><br />{nt.text}</div>)}
        <div className="flex gap-2 mt-3">
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" rows={2} className="bg-black/40 border-white/10" data-testid="admin-note-input" />
          <Button onClick={addNote} className="btn-primary rounded-lg" data-testid="admin-note-save">Add</Button>
        </div>
      </div>
    </div>
  );
};

const UsersList = () => {
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(null);
  useEffect(() => { if (!sel) api.get("/admin/programs/active").then(({ data }) => setRows(data)).catch(() => {}); }, [sel]);
  if (sel) return <UserDetail userId={sel} onBack={() => setSel(null)} />;
  return (
    <div data-testid="prog-users">
      <h1 className="font-serif text-4xl text-white mb-6">Members &amp; Program Editor</h1>
      <div className="glass rounded-2xl overflow-hidden"><div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-white/10">{["Member", "Program", "Status", "Source"].map((c) => <th key={c} className="p-3 text-left text-[10px] font-mono uppercase tracking-widest text-slate-500">{c}</th>)}<th /></tr></thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.id} className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer" onClick={() => setSel(a.user_id)} data-testid={`prog-user-row-${a.id}`}>
                <td className="p-3 text-slate-200">{a.user_name}<div className="text-[11px] text-slate-500 font-mono">{a.user_email}</div></td>
                <td className="p-3 text-slate-300 capitalize">{a.program_type.replace("_", " ")} · {a.duration_weeks}w</td>
                <td className="p-3"><span className="stat-pill">{a.status}</span></td>
                <td className="p-3 font-mono text-xs text-slate-400">{(a.source || "").replace(/_/g, " ")}</td>
                <td className="p-3 text-right"><ChevronLeft size={14} className="rotate-180 text-slate-500 inline" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div></div>
    </div>
  );
};

const Audit = () => {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/programs/audit").then(({ data }) => setRows(data)).catch(() => {}); }, []);
  return (
    <div data-testid="prog-audit">
      <h1 className="font-serif text-4xl text-white mb-6">Program Audit Log</h1>
      <div className="glass rounded-2xl overflow-hidden"><div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-white/10">{["When", "Actor", "Action", "Target"].map((c) => <th key={c} className="p-3 text-left text-[10px] font-mono uppercase tracking-widest text-slate-500">{c}</th>)}</tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-white/5"><td className="p-3 font-mono text-xs text-slate-400">{r.created_at?.slice(0, 19).replace("T", " ")}</td><td className="p-3 text-xs text-slate-300">{r.actor_email}</td><td className="p-3 font-mono text-xs text-cyan-300">{r.action}</td><td className="p-3 font-mono text-[11px] text-slate-500 truncate max-w-[220px]">{r.target}</td></tr>
            ))}
          </tbody>
        </table>
      </div></div>
    </div>
  );
};

export const ProgramsAdmin = ({ mode }) => {
  if (mode === "overview") return <Overview />;
  if (mode === "audit") return <Audit />;
  return <UsersList />;
};
